import { Button, Modal, Progress } from 'antd';
import { Form } from 'antd';
import React, { useEffect, useState } from 'react';
import { createSegment, previewSegment, updateSegment } from 'api/segments';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';
import { App } from 'antd';
import Step1Setup from './Step1Setup';
import Step2Conditions from './Step2Conditions';
import Step3Review from './Step3Review';
import { hasDuplicateConditions } from '../constants';

const STEPS = [
  { title: 'Segment Setup', key: 'setup' },
  { title: 'Define Conditions', key: 'conditions' },
  { title: 'Review & Confirm', key: 'review' },
];

function CreateSegmentModal({
  open,
  onClose,
  onSuccess,
  onCloseSuccess,
  successData,
  onViewSegment,
  segment: initialSegment,
  mode = 'create',
}) {
  const isEditMode = mode === 'edit' && !!initialSegment;
  const [form] = Form.useForm();
  const [step, setStep] = useState(1);
  const [segmentData, setSegmentData] = useState({
    name: '',
    description: '',
    is_auto_update: true,
    condition_logic: 'all',
    conditions: [],
  });
  const [previewCount, setPreviewCount] = useState(null);
  const [creating, setCreating] = useState(false);
  const { message } = App.useApp();

  const isSuccessMode = !!successData;

  useEffect(() => {
    if (!open) {
      setStep(1);
      setSegmentData({ name: '', description: '', is_auto_update: true, condition_logic: 'all', conditions: [] });
      setPreviewCount(null);
      form.resetFields();
    } else if (isEditMode && initialSegment) {
      const data = {
        name: initialSegment.name || '',
        description: initialSegment.description || '',
        is_auto_update: initialSegment.is_auto_update ?? true,
        condition_logic: initialSegment.condition_logic || 'all',
        conditions: initialSegment.conditions || [],
      };
      setSegmentData(data);
      form.setFieldsValue({
        name: data.name,
        description: data.description,
      });
    }
  }, [open, form, isEditMode, initialSegment]);

  const handleCreateNew = () => {
    setStep(1);
    setSegmentData({ name: '', description: '', is_auto_update: true, condition_logic: 'all', conditions: [] });
    setPreviewCount(null);
    form.resetFields();
    onCloseSuccess?.();
  };

  useEffect(() => {
    if (step === 3 && segmentData.conditions?.length > 0) {
      const payload = {
        condition_logic: segmentData.condition_logic,
        conditions: segmentData.conditions,
      };
      previewSegment(payload)
        .then((res) => {
          const data = getData(res)?.data ?? getData(res);
          setPreviewCount(data?.count !== undefined ? data.count : null);
        })
        .catch(() => setPreviewCount(null));
    } else if (step === 3) {
      setPreviewCount(null);
    }
  }, [step, segmentData]);

  const handleContinue = async () => {
    if (step === 1) {
      try {
        const values = await form.validateFields();
        setSegmentData({
          ...segmentData,
          name: values.name,
          description: values.description || '',
          is_auto_update: true,
        });
        setStep(2);
      } catch (e) {
        // validation failed
      }
    } else if (step === 2) {
      if (segmentData.conditions?.length === 0) {
        message.warning('Add at least one condition');
        return;
      }
      if (hasDuplicateConditions(segmentData.conditions)) {
        message.warning('Remove duplicate conditions. Each condition must be unique.');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      handleCreate();
    }
  };

  const handleCreate = () => {
    setCreating(true);
    const promise = isEditMode
      ? updateSegment(initialSegment.id, segmentData)
      : createSegment(segmentData);
    promise
      .then((res) => {
        const data = getData(res)?.data ?? getData(res);
        const created = data;
        const count = previewCount ?? 0;
        onSuccess?.(created, count);
        message.success(isEditMode ? 'Segment updated successfully' : 'Segment created successfully');
        if (isEditMode) {
          onClose?.();
        }
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setCreating(false));
  };

  const handleBack = () => {
    setStep((s) => s - 1);
  };

  if (isSuccessMode) {
    const { segment } = successData;
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={480}
        centered
      >
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }}>✓</div>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{segment?.name}</div>
          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 24 }}>Segment Created Successfully</div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Button
              onClick={() => {
                onClose?.();
                onViewSegment?.(segment?.id);
              }}
            >
              View Segment >
            </Button>
            <Button type="primary" onClick={handleCreateNew}>
              Create New
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={isEditMode ? 'Edit Segment' : 'Create New Segment'}
      open={open}
      onCancel={onClose}
      width={560}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        step > 1 && (
          <Button key="back" onClick={handleBack}>
            {'< Back'}
          </Button>
        ),
        <Button
          key="continue"
          type="primary"
          loading={creating}
          onClick={handleContinue}
        >
          {step < 3 ? 'Continue >' : (isEditMode ? 'Save Changes' : 'Create Segment >')}
        </Button>,
      ].filter(Boolean)}
    >
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, color: '#717679', marginBottom: 4 }}>
          Step {step} of 3
        </div>
        <Progress
          percent={(step / 3) * 100}
          showInfo={false}
          strokeColor="#672792"
        />
        <div style={{ fontWeight: 600, marginTop: 4 }}>{STEPS[step - 1].title}</div>
      </div>
      {step === 1 && <Step1Setup form={form} />}
      {step === 2 && (
        <Step2Conditions segmentData={segmentData} onChange={setSegmentData} />
      )}
      {step === 3 && (
        <Step3Review segmentData={segmentData} />
      )}
    </Modal>
  );
}

export default CreateSegmentModal;
